/**
 * Панель каталога: слева колонка разделов, справа компактный список типов предметов
 * с поиском и подгруппами.
 *
 * Клик по карточке ставит новый объект. Но если выделен объект того же вида — предмет
 * на полу, на стене или светильник, — карточка заменяет его тип на месте
 * (`replaceSelection`, так же, как в 3D): выделил кровать, выбрал другую — она встала
 * вместо прежней. Пока замена возможна, над карточками об этом сказано.
 *
 * Карточку предмета можно и перетащить на чертёж (`useCatalogCardDrag`): отпустил —
 * предмет встал там, где был призрак. Пол, потолок, отделку и прочее, что назначается
 * комнате, не тащат — их ставят кликом по комнате. На развёртках карточки тоже не тащат.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { routerPaths } from "@/app/routerPaths";
import {
	CATALOG,
	CATALOG_SEARCH_MIN_LENGTH,
	PLACEABLE_SECTION_KEYS,
	catalogCards,
	catalogSection,
	getCatalogItem,
	searchCatalog,
	useCustomFurnitureStore,
	type CatalogItemBase,
	type CatalogSectionKey,
	type View3D,
} from "@/shared/catalog";
import { CatalogSectionKind, LayerKind, ROOM_LAYERS } from "@/shared/plan";
import { useToggleVisibleLayer } from "@/shared/hooks";
import { useProjectStore } from "@/shared/store";
import { UIKit } from "@/shared/ui";
import { Tool, ViewMode, useEditorStore } from "@/widgets/editor";
import { addLightingLink, autoLightingConnections } from "@/widgets/electrical";
import {
	CatalogDragChip,
	type CatalogCardDragProps,
	replaceSelection,
	useCatalogCardDrag,
} from "@/widgets/place-fixture";
import {
	ModelPeek,
	ModelPreview,
	ModelThumbsHost,
	PreviewEyeButton,
	useModelPeek,
} from "@/widgets/model-preview";
import { formatLengthNumber } from "@/shared/lib/geometry";
import { useLengthUnit } from "@/shared/store";
import { CatalogTile } from "./CatalogTile";
import { CatalogNavIcon, type CatalogNavIconName } from "./CatalogNavIcon";

/* ─── Иконки разделов ─── */

interface SectionMeta {
	icon: CatalogNavIconName;
	short: string;
}

const SECTION_META: Partial<Record<CatalogSectionKind, SectionMeta>> = {
	[CatalogSectionKind.Wall]: { icon: "brick", short: "Стены" },
	[CatalogSectionKind.Door]: { icon: "door", short: "Двери" },
	[CatalogSectionKind.Window]: { icon: "window", short: "Окна" },
	[CatalogSectionKind.Cable]: { icon: "cable", short: "Кабель" },
	[CatalogSectionKind.Electrical]: { icon: "socket", short: "Электр." },
	[CatalogSectionKind.Lighting]: { icon: "bulb", short: "Свет" },
	[CatalogSectionKind.Furniture]: { icon: "armchair", short: "Мебель" },
	[CatalogSectionKind.Decor]: { icon: "picture", short: "Декор" },
	[CatalogSectionKind.CustomFurniture]: { icon: "wardrobe", short: "Моя" },
	[CatalogSectionKind.Plumbing]: { icon: "bath", short: "Сантех." },
	[CatalogSectionKind.Pipe]: { icon: "pipe", short: "Трубы" },
	[CatalogSectionKind.Water]: { icon: "faucet", short: "Вода" },
	[CatalogSectionKind.Floor]: { icon: "tile", short: "Пол" },
	[CatalogSectionKind.Finish]: { icon: "roller", short: "Отделка" },
	[CatalogSectionKind.Waterproof]: { icon: "droplet", short: "Гидро" },
	[CatalogSectionKind.Screed]: { icon: "ruler", short: "Стяжка" },
	[CatalogSectionKind.Ceiling]: { icon: "ceiling", short: "Потолок" },
	[CatalogSectionKind.Baseboard]: { icon: "ruler", short: "Плинтус" },
};

const SUB_GROUP_ICONS: Record<string, CatalogNavIconName> = {
	Спальня: "bed",
	Гостиная: "sofa",
	Кухня: "kitchen",
	Кабинет: "monitor",
	Хранение: "archive",
	Детская: "baby",
	Техника: "tv",
	Спорт: "dumbbell",
	Балкон: "window",
	Ковры: "rug",
	Картины: "picture",
	Растения: "plant",
	Питомцы: "paw",
	Книги: "archive",
	"Рабочее место": "monitor",
	Хобби: "guitar",
	Кинотеатр: "film",
	Интерьер: "frame",
	Плитка: "tile",
	Кварцвинил: "ruler",
	Розетки: "socket",
	Слаботочка: "cable",
	Выключатели: "switch",
	Распредкоробки: "box",
	Краска: "roller",
	Обои: "frame",
	Штукатурка: "brick",
	Санузел: "faucet",
	Ванная: "bath",
	Модули: "box",
	Шкафы: "wardrobe",
	Силовой: "cable",
	Слаботочный: "cable",
	Натяжной: "ceiling",
	Гипсокартон: "ceiling",
	Стояки: "pipe",
	Коллекторы: "faucet",
	Водорозетки: "socket",
	Окна: "window",
	Балконные: "door",
	ПВХ: "tile",
	Дерево: "archive",
	МДФ: "archive",
	Алюминий: "ruler",
	Теневой: "ruler",
	Полипропилен: "pipe",
	"Сшитый полиэтилен": "pipe",
	Металлопластик: "pipe",
	Сухая: "tile",
	Полусухая: "tile",
	Пескобетонная: "brick",
	Обмазочная: "roller",
	Рулонная: "rug",
	Наплавляемая: "droplet",
};

/** В 3D можно ставить только объекты, у которых есть представление в сцене. */
const THREE_D_CATALOG_SECTIONS = new Set<CatalogSectionKind>([
	CatalogSectionKind.Furniture,
	CatalogSectionKind.Decor,
	CatalogSectionKind.CustomFurniture,
	CatalogSectionKind.Plumbing,
	CatalogSectionKind.Electrical,
	CatalogSectionKind.Lighting,
	CatalogSectionKind.Water,
]);

/**
 * Разделы каталога на плане: сначала предметы — мебель первой, её ищут чаще всего, — потом
 * то, что кладут на комнату целиком (пол, стяжка, потолок…), за чертой `ROOM_SECTIONS_START`.
 * Отделку стен в 2D не выбирают — её кладут на стены в 3D, в панели «Отделка».
 */
const SECTION_ORDER: CatalogSectionKey[] = [
	CatalogSectionKind.Furniture,
	CatalogSectionKind.Decor,
	CatalogSectionKind.CustomFurniture,
	CatalogSectionKind.Plumbing,
	CatalogSectionKind.Water,
	CatalogSectionKind.Electrical,
	CatalogSectionKind.Lighting,
	CatalogSectionKind.Floor,
	CatalogSectionKind.Waterproof,
	CatalogSectionKind.Screed,
	CatalogSectionKind.Ceiling,
	CatalogSectionKind.Baseboard,
];
/** Первый раздел «на комнату» — перед ним черта */
const ROOM_SECTIONS_START: CatalogSectionKey = CatalogSectionKind.Floor;
const placeable: CatalogSectionKey[] = PLACEABLE_SECTION_KEYS.filter(
	(key) => key !== CatalogSectionKind.Finish,
);
const PLAN_CATALOG_SECTIONS = [
	...SECTION_ORDER.filter((key) => placeable.includes(key)),
	// Новый раздел, которого нет в порядке, не теряется — встаёт в конец
	...placeable.filter((key) => !SECTION_ORDER.includes(key)),
];

/**
 * Слои, чьи объекты можно перетащить на чертёж: электрика и свет, мебель и декор,
 * сантехника и точки воды. Пол, потолок, стяжку, отделку назначают комнате кликом,
 * а кабели и трубы прокладывают своими инструментами — их не тащат.
 */
const DRAGGABLE_LAYERS = new Set([
	LayerKind.Electrical,
	LayerKind.Furniture,
	LayerKind.Plumbing,
	LayerKind.Floors,
	LayerKind.Waterproof,
	LayerKind.Screed,
	LayerKind.Ceiling,
	LayerKind.Baseboard,
]);

const variantCount = (catalog: CatalogSectionKey, item: CatalogItemBase) =>
	item.family
		? CATALOG[catalog].items.filter((candidate) => candidate.family === item.family).length
		: 1;

/* ─── Компонент ─── */

export const CatalogDrawer = () => {
	const activeCatalog = useEditorStore((s) => s.activeCatalog);
	const setActiveCatalog = useEditorStore((s) => s.setActiveCatalog);
	const placing = useEditorStore((s) => s.placing);
	const setPlacing = useEditorStore((s) => s.setPlacing);
	const setTool = useEditorStore((s) => s.setTool);
	const setDrawer = useEditorStore((s) => s.setDrawer);
	const selection = useEditorStore((s) => s.selection);
	const setSelection = useEditorStore((s) => s.setSelection);
	const tool = useEditorStore((s) => s.tool);
	const showLightingConnections = useEditorStore((s) => s.showLightingConnections);
	const lightingConnectionStyle = useEditorStore((s) => s.lightingConnectionStyle);
	const toggleLightingConnections = useEditorStore((s) => s.toggleLightingConnections);
	const setLightingConnectionStyle = useEditorStore((s) => s.setLightingConnectionStyle);
	const toggleVisibleLayer = useToggleVisibleLayer();
	const viewMode = useEditorStore((s) => s.viewMode);
	const { cardDragProps, chip } = useCatalogCardDrag();
	// Навёл на строку предмета — рядом его модель в 3D. У разделов без объёма (пол) просмотра нет
	const { peek, peekProps, closePeek } = useModelPeek();
	const peekFor = (sectionKey: CatalogSectionKey, id: string, title: string) => {
		const full = getCatalogItem({ catalog: sectionKey, item: id });
		return full ? peekProps(full, title) : undefined;
	};
	// «Глазик» на строке: модель крупно в окне — на планшете он виден всегда, наведения там нет
	const [preview, setPreview] = useState<View3D | null>(null);
	const previewFor = (sectionKey: CatalogSectionKey, id: string) => {
		const full = getCatalogItem({ catalog: sectionKey, item: id });
		return full
			? () => {
					closePeek();
					setPreview(full.view3d);
				}
			: undefined;
	};
	const isThreeD = viewMode === ViewMode.ThreeD;
	const navigate = useNavigate();
	// «Моя мебель» собирается из хранилища — подписка перерисует раздел, когда шкафов станет больше
	const customCount = useCustomFurnitureStore(
		(s) => s.designs.length + s.kitchens.length + s.modules.length,
	);

	useEffect(() => {
		if (
			(isThreeD && !THREE_D_CATALOG_SECTIONS.has(activeCatalog)) ||
			(!isThreeD && activeCatalog === CatalogSectionKind.Finish)
		) {
			setActiveCatalog(CatalogSectionKind.Furniture);
		}
	}, [activeCatalog, isThreeD, setActiveCatalog]);
	/** Что карточка поставит при перетаскивании; null — её не тащат */
	const dragRef = (catalog: CatalogSectionKey, itemId: string) =>
		viewMode !== ViewMode.Elevation && DRAGGABLE_LAYERS.has(CATALOG[catalog].kind)
			? { catalog, item: itemId }
			: null;

	const [subGroup, setSubGroup] = useState<string | null>(null);
	const [search, setSearch] = useState("");
	/** Раскрытый тип предмета: его модели карточками («Диван · 17 моделей» → все диваны) */
	const [openFamily, setOpenFamily] = useState<string | null>(null);
	const unit = useLengthUnit();
	/** Разделы с объёмом показываем карточками со снимком модели, остальные — строками */
	const withTiles = (key: CatalogSectionKey) => THREE_D_CATALOG_SECTIONS.has(key);
	/** Размер модели под названием: ширина × глубина (у настенного — × высота) */
	const sizeLabel = (key: CatalogSectionKey, id: string) => {
		const full = getCatalogItem({ catalog: key, item: id });
		if (!full) return undefined;
		const second = full.depth > 30 ? full.depth : full.height;
		return `${formatLengthNumber(full.width, unit)} × ${formatLengthNumber(second, unit)}`;
	};

	const section = catalogSection(activeCatalog);
	const groups = section.subGroups;
	const cardsInGroup = (group: string) =>
		catalogCards(
			section,
			section.items.filter((item) => item.subGroup === group),
		);
	const groupsWithChoices = groups.filter((group) => cardsInGroup(group).length > 1);
	const shouldShowGroups = groupsWithChoices.length > 0;
	const selectedSubGroup =
		shouldShowGroups && subGroup !== null && groupsWithChoices.includes(subGroup)
			? subGroup
			: groupsWithChoices[0];

	const isSearching = search.trim().length >= CATALOG_SEARCH_MIN_LENGTH;
	const searchHits = isSearching
		? searchCatalog(search, isThreeD ? [...THREE_D_CATALOG_SECTIONS] : PLAN_CATALOG_SECTIONS)
		: [];

	const sectionItems =
		shouldShowGroups && selectedSubGroup
			? section.items.filter((item) => item.subGroup === selectedSubGroup)
			: section.items;
	// Одна карточка на тип предмета: модели одного типа выбирают у поставленного («Варианты»)
	const sectionCards = catalogCards(section, sectionItems);
	/** Тип элемента, который сейчас ставится, — чтобы подсветить карточку его типа */
	const placingFamily = section.items.find((item) => item.id === placing?.item)?.family;
	/** Модели раскрытого типа; null — тип не раскрыт или в разделе его нет */
	const familyItems = openFamily
		? section.items.filter((item) => item.family === openFamily)
		: null;
	const familyTitle =
		section.families?.find((family) => family.id === openFamily)?.name ?? familyItems?.[0]?.name;
	const handleSectionChange = (key: CatalogSectionKey) => {
		setActiveCatalog(key);
		setSubGroup(null);
		setSearch("");
		setOpenFamily(null);

		const layerKind = CATALOG[key].kind;
		if (layerKind === LayerKind.Ceiling) {
			toggleVisibleLayer(LayerKind.Waterproof, false);
			toggleVisibleLayer(LayerKind.Screed, false);
			toggleVisibleLayer(LayerKind.Ceiling, true);
		} else if (ROOM_LAYERS.includes(layerKind)) {
			for (const kind of ROOM_LAYERS) toggleVisibleLayer(kind, kind === layerKind);
		}
	};

	const handlePlaceItem = (catalog: CatalogSectionKey, itemId: string) => {
		const ref = { catalog, item: itemId };
		const replaced = replaceSelection(selection, ref);
		if (replaced) {
			setSelection(replaced);
			if (window.matchMedia("(max-width: 767px)").matches) setDrawer(null);
			return;
		}
		setTool(Tool.Place);
		setPlacing(ref);
		if (window.matchMedia("(max-width: 767px)").matches) setDrawer(null);
	};

	const selectLightingConnectionTool = () => {
		if (!showLightingConnections) toggleLightingConnections();
		setTool(Tool.LightingConnection);
		setSelection(null);
	};

	const autoConnectLights = () => {
		const layers = useProjectStore.getState().project.layers;
		const connections = autoLightingConnections(
			layers.walls,
			layers.electrical,
			layers.wiring.connections,
			lightingConnectionStyle,
		);
		if (connections.length === 0) return;
		useProjectStore.getState().recordEdit(() => {
			for (const connection of connections) {
				addLightingLink(
					layers.electrical,
					layers.wiring.items,
					connection.from,
					connection.to,
					connection.style,
				);
			}
		});
		if (!showLightingConnections) toggleLightingConnections();
	};

	const activeSectionMeta = SECTION_META[activeCatalog];

	return (
		<div className="flex h-full min-h-0 flex-col overflow-hidden md:flex-row">
			{/* ── Колонка разделов слева ── */}
			<div className="flex shrink-0 gap-1 overflow-x-auto border-b border-border bg-bg px-2 py-1.5 md:flex-col md:overflow-x-hidden md:overflow-y-auto md:border-r md:border-b-0 md:px-1 md:py-2">
				{PLAN_CATALOG_SECTIONS.map((key) => {
					const meta = SECTION_META[key];
					if (!meta) return null;
					const unavailableInThreeD = isThreeD && !THREE_D_CATALOG_SECTIONS.has(key);
					const active = activeCatalog === key && !isSearching;
					const sectionButton = (
						<button
							key={key}
							type="button"
							title={CATALOG[key].label}
							disabled={unavailableInThreeD}
							className={`flex min-w-14 flex-col items-center gap-0.5 rounded-md px-1 py-1.5 text-xs leading-tight transition-colors duration-150 md:min-w-0${
								unavailableInThreeD
									? " cursor-not-allowed text-ink/40"
									: active
										? " bg-accent-soft font-bold text-ink"
										: " font-bold text-ink hover:bg-accent-soft hover:text-ink"
							}`}
							onClick={() => handleSectionChange(key)}
						>
							<CatalogNavIcon name={meta.icon} size={17} />
							<UIKit.Text as="span" className="w-full text-center">
								{meta.short}
							</UIKit.Text>
						</button>
					);
					const divider =
						key === ROOM_SECTIONS_START ? (
							<div
								key={`${key}-divider`}
								aria-hidden
								className="w-px shrink-0 self-stretch bg-border md:my-1 md:h-px md:w-auto"
							/>
						) : null;
					const button = unavailableInThreeD ? (
						<UIKit.Tooltip key={key} className="flex w-full" content="Недоступно в 3D" side="right">
							<UIKit.Text as="span" className="flex w-full">
								{sectionButton}
							</UIKit.Text>
						</UIKit.Tooltip>
					) : (
						sectionButton
					);
					return divider ? [divider, button] : button;
				})}
			</div>

			{/* ── Правая часть: поиск, подгруппы, элементы ── */}
			<div className="flex min-w-0 flex-1 flex-col overflow-hidden">
				{/* Поиск */}
				<div className="shrink-0 border-b border-border px-3 py-2">
					<UIKit.SearchInput
						value={search}
						onChange={(value) => {
							setSearch(value);
							setOpenFamily(null);
						}}
					/>
				</div>

				{isSearching ? (
					/* ── Результаты поиска ── */
					<div className="min-h-0 flex-1 overflow-y-auto p-2">
						{searchHits.length === 0 ? (
							<UIKit.Text className="px-3 py-8 text-center text-xs text-ink">
								Ничего не найдено
							</UIKit.Text>
						) : (
							<>
								{/* Предметы с объёмом — карточками со снимком, остальное — строками */}
								<div className="grid grid-cols-2 gap-1 @md:grid-cols-3">
									{searchHits
										.filter((hit) => withTiles(hit.sectionKey))
										.map((hit) => {
											const full = getCatalogItem({ catalog: hit.sectionKey, item: hit.item.id });
											if (!full) return null;
											const variants = variantCount(hit.sectionKey, hit.item);
											return (
												<CatalogTile
													key={`${hit.sectionKey}-${hit.item.id}`}
													item={full}
													title={hit.title}
													meta={
														variants > 1
															? `${CATALOG[hit.sectionKey].label} · ${modelsLabel(variants)}`
															: CATALOG[hit.sectionKey].label
													}
													isActive={
														tool === Tool.Place &&
														placing?.catalog === hit.sectionKey &&
														placing.item === hit.item.id
													}
													onClick={() => handlePlaceItem(hit.sectionKey, hit.item.id)}
													dragProps={cardDragProps(dragRef(hit.sectionKey, hit.item.id), hit.title)}
													peekProps={peekFor(hit.sectionKey, hit.item.id, hit.title)}
													onPreview={previewFor(hit.sectionKey, hit.item.id)}
												/>
											);
										})}
								</div>
								<div className="space-y-0.5">
									{searchHits
										.filter((hit) => !withTiles(hit.sectionKey))
										.map((hit) => (
											<ItemCard
												key={`${hit.sectionKey}-${hit.item.id}`}
												item={hit.item}
												isActive={
													tool === Tool.Place &&
													placing?.catalog === hit.sectionKey &&
													placing.item === hit.item.id
												}
												title={hit.title}
												badge={CATALOG[hit.sectionKey].label}
												variants={variantCount(hit.sectionKey, hit.item)}
												onClick={() => handlePlaceItem(hit.sectionKey, hit.item.id)}
												dragProps={cardDragProps(dragRef(hit.sectionKey, hit.item.id), hit.title)}
												peekProps={peekFor(hit.sectionKey, hit.item.id, hit.title)}
												onPreview={previewFor(hit.sectionKey, hit.item.id)}
											/>
										))}
								</div>
							</>
						)}
					</div>
				) : (
					<>
						{!isThreeD && activeCatalog === CatalogSectionKind.Lighting && (
							<div className="flex shrink-0 flex-col gap-2 rounded-lg border border-border bg-bg p-2">
								<UIKit.Button
									type="button"
									className={`flex items-center justify-between gap-2 text-left text-xs font-medium${
										showLightingConnections ? " text-accent" : " text-ink"
									}`}
									aria-pressed={showLightingConnections}
									onClick={toggleLightingConnections}
								>
									Соединительные линии
									<UIKit.Text as="span" className="text-xs font-normal">
										{showLightingConnections ? "Вкл." : "Выкл."}
									</UIKit.Text>
								</UIKit.Button>
								<UIKit.Button
									type="button"
									className={`rounded-md px-2 py-1 text-xs font-medium${
										tool === Tool.LightingConnection
											? " bg-accent-soft text-accent"
											: " bg-surface text-ink hover:bg-accent-soft"
									}`}
									aria-pressed={tool === Tool.LightingConnection}
									onClick={selectLightingConnectionTool}
								>
									Провести линию
								</UIKit.Button>
								<UIKit.Button
									type="button"
									className="rounded-md bg-surface px-2 py-1 text-xs font-medium text-ink hover:bg-accent-soft"
									onClick={autoConnectLights}
								>
									Auto
								</UIKit.Button>
								<div className="flex gap-1" aria-label="Способ рисования соединений">
									{(
										[
											["arc", "Дугами"],
											["line", "Линиями"],
										] as const
									).map(([style, label]) => (
										<UIKit.Button
											key={style}
											type="button"
											className={`flex-1 rounded-md px-2 py-1 text-xs${
												lightingConnectionStyle === style
													? " bg-accent-soft font-medium text-accent"
													: " bg-surface text-ink hover:bg-accent-soft"
											}`}
											aria-pressed={lightingConnectionStyle === style}
											onClick={() => setLightingConnectionStyle(style)}
										>
											{label}
										</UIKit.Button>
									))}
								</div>
							</div>
						)}

						{activeCatalog === CatalogSectionKind.CustomFurniture && (
							<div className="flex shrink-0 flex-col gap-1.5 rounded-lg border border-border bg-bg p-2">
								{customCount === 0 && (
									<UIKit.Text className="text-xs leading-snug text-ink">
										Здесь появятся шкафы и кухни, собранные в конструкторе мебели: под ваши размеры,
										со своими модулями и цветами.
									</UIKit.Text>
								)}
								<UIKit.Button
									type="button"
									className="rounded-md bg-surface px-2 py-1 text-xs font-medium text-accent hover:bg-accent-soft"
									onClick={() => void navigate(routerPaths.furnitureGenerator)}
								>
									+ Собрать мебель
								</UIKit.Button>
							</div>
						)}
						<div className="flex min-h-0 flex-1 flex-col md:flex-row">
							{shouldShowGroups && (
								<div className="flex shrink-0 gap-1 overflow-x-auto border-b border-border p-2 md:w-fit md:flex-col md:overflow-x-hidden md:overflow-y-auto md:border-r md:border-b-0 md:px-2">
									{groups.map((g) => {
										const groupIcon = SUB_GROUP_ICONS[g] ?? activeSectionMeta?.icon ?? "box";
										const groupCards = cardsInGroup(g);
										const isDirectItem = groupCards.length === 1;
										return (
											<button
												key={g}
												type="button"
												className={`flex shrink-0 items-center gap-1.5 rounded-md px-2 py-1.5 text-left text-xs leading-tight transition-colors duration-150${
													!isDirectItem && selectedSubGroup === g
														? " bg-accent-soft font-bold text-ink"
														: " font-bold text-ink hover:bg-accent-soft hover:text-ink"
												}`}
												onClick={() => {
													if (isDirectItem) {
														handlePlaceItem(activeCatalog, groupCards[0].item.id);
														return;
													}
													setSubGroup(g);
													setOpenFamily(null);
												}}
											>
												<CatalogNavIcon name={groupIcon} size={14} />
												<UIKit.Text as="span" className="whitespace-nowrap">
													{g}
												</UIKit.Text>
											</button>
										);
									})}
								</div>
							)}
							{withTiles(activeCatalog) ? (
								<div className="@container min-h-0 min-w-0 flex-1 overflow-y-auto p-2">
									{familyItems ? (
										<>
											{/* Раскрытый тип: все его модели — выбирают глазами и ставят кликом */}
											<div className="mb-2 flex items-center gap-2">
												<UIKit.Button
													type="button"
													title="Назад ко всем типам"
													className="flex items-center gap-1 rounded-md px-1.5 py-1 text-xs font-medium text-accent hover:bg-accent-soft"
													onClick={() => setOpenFamily(null)}
												>
													<UIKit.Text as="span" aria-hidden>
														←
													</UIKit.Text>
													Назад
												</UIKit.Button>
												<UIKit.Text
													as="span"
													className="min-w-0 truncate text-xs font-bold text-ink"
												>
													{familyTitle} · {modelsLabel(familyItems.length)}
												</UIKit.Text>
											</div>
											<div className="grid grid-cols-2 gap-1 @md:grid-cols-3">
												{familyItems.map((item) => {
													const full = getCatalogItem({ catalog: activeCatalog, item: item.id });
													if (!full) return null;
													return (
														<CatalogTile
															key={`${activeCatalog}-${item.id}`}
															item={full}
															title={item.name}
															meta={sizeLabel(activeCatalog, item.id)}
															isActive={
																tool === Tool.Place &&
																placing?.catalog === activeCatalog &&
																placing.item === item.id
															}
															onClick={() => handlePlaceItem(activeCatalog, item.id)}
															dragProps={cardDragProps(dragRef(activeCatalog, item.id), item.name)}
															peekProps={peekFor(activeCatalog, item.id, item.name)}
															onPreview={previewFor(activeCatalog, item.id)}
														/>
													);
												})}
											</div>
										</>
									) : (
										<div className="grid grid-cols-2 gap-1 @md:grid-cols-3">
											{sectionCards.map(({ item, title, variants }) => {
												const full = getCatalogItem({ catalog: activeCatalog, item: item.id });
												if (!full) return null;
												const family = variants > 1 ? item.family : undefined;
												return (
													<CatalogTile
														key={`${activeCatalog}-${item.id}`}
														item={full}
														title={title}
														meta={
															family ? modelsLabel(variants) : sizeLabel(activeCatalog, item.id)
														}
														isActive={
															tool === Tool.Place &&
															placing?.catalog === activeCatalog &&
															(placing.item === item.id ||
																(item.family !== undefined && item.family === placingFamily))
														}
														// Тип с несколькими моделями раскрывается, одиночный предмет — ставится
														onClick={() =>
															family
																? setOpenFamily(family)
																: handlePlaceItem(activeCatalog, item.id)
														}
														dragProps={cardDragProps(dragRef(activeCatalog, item.id), title)}
														peekProps={peekFor(activeCatalog, item.id, title ?? item.name)}
														onPreview={previewFor(activeCatalog, item.id)}
													/>
												);
											})}
										</div>
									)}
								</div>
							) : (
								<div className="min-h-0 min-w-0 flex-1 space-y-0.5 overflow-y-auto p-2">
									{sectionCards.map(({ item, title, variants }) => (
										<ItemCard
											key={`${activeCatalog}-${item.id}`}
											item={item}
											title={title}
											variants={variants}
											isActive={
												tool === Tool.Place &&
												placing?.catalog === activeCatalog &&
												(placing.item === item.id ||
													(item.family !== undefined && item.family === placingFamily))
											}
											onClick={() => handlePlaceItem(activeCatalog, item.id)}
											dragProps={cardDragProps(dragRef(activeCatalog, item.id), title)}
											peekProps={peekFor(activeCatalog, item.id, title ?? item.name)}
											onPreview={previewFor(activeCatalog, item.id)}
										/>
									))}
								</div>
							)}
						</div>
					</>
				)}
			</div>

			<CatalogDragChip chip={chip} />
			<ModelThumbsHost />
			{peek && <ModelPeek peek={peek} />}
			{preview && <ModelPreview view3d={preview} onClose={() => setPreview(null)} />}
		</div>
	);
};

/** «11 моделей», «4 модели», «21 модель» — сколько моделей в типе. */
const modelsLabel = (count: number) => {
	const tens = count % 100;
	const ones = count % 10;
	const word =
		tens >= 11 && tens <= 14
			? "моделей"
			: ones === 1
				? "модель"
				: ones >= 2 && ones <= 4
					? "модели"
					: "моделей";
	return `${count} ${word}`;
};

/* ─── Строка элемента ─── */

const ItemCard = ({
	item,
	title,
	variants,
	isActive,
	badge,
	onClick,
	dragProps,
	peekProps,
	onPreview,
}: {
	item: CatalogItemBase;
	/** Название карточки: тип предмета или конкретная модель */
	title?: string;
	/** Сколько моделей объединено в типе. */
	variants: number;
	isActive: boolean;
	badge?: string;
	onClick: () => void;
	/** Перетаскивание карточки на чертёж — см. useCatalogCardDrag */
	dragProps: CatalogCardDragProps;
	/** Просмотр модели по наведению — см. useModelPeek; нет — у предмета нет объёма */
	peekProps?: ReturnType<ReturnType<typeof useModelPeek>["peekProps"]>;
	/** «Глазик»: модель крупно в окне; нет — у предмета нет объёма */
	onPreview?: () => void;
}) => {
	return (
		// Строка — название и «×N» кнопкой, глазик рядом отдельной кнопкой (кнопка в кнопке
		// недопустима); подсветка — на всю строку
		<div
			className={`group flex items-center gap-1 rounded-md pr-1.5 transition-colors duration-150${
				isActive ? " bg-accent-soft" : " hover:bg-bg"
			}`}
			{...peekProps}
		>
			<button
				type="button"
				title={item.description}
				className="flex min-w-0 flex-1 flex-col items-start gap-0.5 p-2 text-left text-xs text-ink"
				onClick={onClick}
				{...dragProps}
			>
				{/* Название на всю ширину строки, под ним мелко — раздел (в поиске) и сколько моделей:
				    в узкой панели планшета счётчик сбоку наезжал на длинное название */}
				<UIKit.Text
					as="span"
					className="w-full leading-snug font-bold wrap-break-word hyphens-auto text-ink"
				>
					{title ?? item.name}
				</UIKit.Text>
				{(badge || variants > 1) && (
					<UIKit.Text as="span" className="text-xs leading-none text-ink/60">
						{[badge, variants > 1 ? modelsLabel(variants) : null].filter(Boolean).join(" · ")}
					</UIKit.Text>
				)}
			</button>
			{onPreview && <PreviewEyeButton onClick={onPreview} inline />}
		</div>
	);
};
